import { environment } from '~env/environment';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';
import type { AutocompleteItem } from '~shared/modules/advanced-form/components/autocomplete/autocomplete.component';
import { combineLatest, of } from 'rxjs';
import type { Observable } from 'rxjs';
import { DEFAULT_COUNTRIES_PER_MARKET } from '../../src/app/shared/constants/country-code.enum';
import { ApiConfig, NUMBER_1 } from '~shared/constants';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import {
  FF_SL_CODE_LOGIN_DISABLE_SR,
  FF_SL_DISABLE_SILENT_LOGIN,
  SL_USER_LOGIN_TYPE_KEY,
} from '~app/modules/authorization/constants/authorization.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { OAuthService } from 'angular-oauth2-oidc';
import { SOFT_LOGIN_TOKEN_KEY, SUB_ACCOUNT_TYPE_KEY } from '~app/core/modules/identity/constants/identity.constant';
import { CountryBasedTierTwoConfigService } from '~app/modules/authorization/services/country-based-tier-two-config.service';
import { ApplicationEventType } from '~shared/modules/application-event/constants';

// Add a variable to store the GTM service reference
let dispatchGAEvent;

// Add the setGtmService function to set the GTM service reference
export function setGAService(service) {
  dispatchGAEvent = service.gtagEvent.bind(service);
}

// Track the first click on the address field
export function trackAddressFieldClick() {
  if (CountryBasedTierTwoConfigService.isFirstAddressSearchClick) {
    dispatchGAEvent?.({
      event: 'se_using_address_search_field',
      action: 'using_address_search_field',
      label: 'address search field clicked',
      category: ApplicationEventType.Interaction,
    });
    CountryBasedTierTwoConfigService.isFirstAddressSearchClick = false;
  }
}

export async function syncSSOForReactApp({ tokenType = 'Bearer', accessToken, expiresAt }) {
  if (accessToken) {
    const payload = {
      token_type: tokenType,
      access_token: accessToken,
      expires_at: expiresAt,
    };
    const baseHrefWithoutTrailingSlash = environment.baseHref().replace(new RegExp('/$'), '');

    const syncSSORequest = fetch(`${baseHrefWithoutTrailingSlash}/app-api/forceLogin?${new URLSearchParams(payload)}`, {
      method: 'GET',
    })
      .then((res) => res.text())
      .catch(() => null);

    await syncSSORequest;
  }
}

export function isMMCompanion() {
  return navigator?.userAgent?.includes('Companion');
}

export function isOTPLoggedIn() {
  return localStorage?.getItem(SL_USER_LOGIN_TYPE_KEY)?.toUpperCase() === 'CODE_LOGIN';
}

export function isSoftLoggedIn() {
  return (
    !window?.sessionStorage?.getItem('access_token') &&
    localStorage?.getItem(SL_USER_LOGIN_TYPE_KEY)?.toUpperCase() === 'SOFT_LOGIN'
  );
}

export function isHeadQuarter() {
  return localStorage?.getItem(SUB_ACCOUNT_TYPE_KEY)?.toLowerCase() === 'head_quarter';
}

export function isFallbackFlyoutMode() {
  return isMMCompanion() || isOTPLoggedIn() || isSoftLoggedIn();
}

export function isNotSSO() {
  return isOTPLoggedIn() || isSoftLoggedIn();
}

export function redirectTo(redirectURL: string) {
  window.location.href = redirectURL;
}

export function getLoginType() {
  // Check if the user is soft logged in
  if (isSoftLoggedIn()) {
    return 'soft-login';
  }

  // Check if there is no access token in session storage
  const accessToken = sessionStorage.getItem('access_token');
  if (!accessToken) {
    return 'anonymous';
  }

  // Check if the user is logged in via OTP
  if (isOTPLoggedIn()) {
    return 'code-login';
  }

  // Check for SSO login
  if (!isNotSSO() && accessToken) {
    return 'sso-login';
  }

  // Fallback case (if none of the above conditions are met)
  return 'unknown';
}

export function deleteSoftLoginSession() {
  localStorage.removeItem(SL_USER_LOGIN_TYPE_KEY);
  localStorage.removeItem(SOFT_LOGIN_TOKEN_KEY);
}

export function maskEmail(email) {
  // Regex to match the email parts: local part and domain
  return email.replace(/^(.{3})(.*)(.{3}@.*)$/, function (_, firstThree, middleChars, lastThreeAndDomain) {
    // Mask the middle characters of the local part, keeping first and last 3 characters visible
    return firstThree + middleChars.replace(/./g, '*') + lastThreeAndDomain;
  });
}

export function generateIsSilentAuthDisabled$(
  configCatService: ActiveFeaturesConfigcatService,
  oauthService: OAuthService
) {
  return combineLatest([
    configCatService.isFeatureEnabledReadyForCurrentCountry$(FF_SL_DISABLE_SILENT_LOGIN),
    configCatService.isFeatureEnabledReadyForCurrentCountry$(FF_SL_CODE_LOGIN_DISABLE_SR),
  ]).pipe(
    take(NUMBER_1),
    map(
      ([isMMCompanionEnabled, isOtpLoginEnabled]) =>
        (isMMCompanionEnabled && isMMCompanion()) || (isOtpLoginEnabled && isOTPLoggedIn()) || isSoftLoggedIn()
    ),
    tap((isDisabled) => {
      // eslint-disable-next-line no-console
      console.log(`[generateIsSilentAuthDisabled$] silent login ${isDisabled ? 'disabled' : 'enabled'}`);

      if (isDisabled) {
        oauthService.useSilentRefresh = false;
        oauthService.sessionChecksEnabled = false;
        oauthService.silentRefreshRedirectUri = undefined;
      }
    }),
    catchError(() => of(false))
  );
}

export function cleanupAuthSession() {
  localStorage.removeItem(SL_USER_LOGIN_TYPE_KEY);
}

export function searchAddress(text: string): Observable<AutocompleteItem[]> {
  return fromFetch(ApiConfig.SVC_BUYER_ACCOUNT_ADDRESS_SUGGESTIONS_URL(text), {
    headers: { 'Country-Code': MarketDetectionUtil.getCountryCode() },
  }).pipe(
    switchMap((res) => res.json()),
    map((data) =>
      data.suggestions.map((suggestion) => ({
        label: suggestion.address,
        value: suggestion.placeId,
      }))
    )
  );
}

export async function onAddressOptionClick(item: AutocompleteItem) {
  const event = new Event('input', { bubbles: true });
  const addressSearchField = document.getElementById('mm-input-address-search-field') as HTMLInputElement;
  if (addressSearchField) {
    addressSearchField.value = item.label;
    addressSearchField.dispatchEvent(event);
  }
  const addressDetails = await fetch(ApiConfig.SVC_BUYER_ACCOUNT_ADDRESS_DETAILS_URL(item.value), {
    headers: { 'Country-Code': MarketDetectionUtil.getCountryCode() },
  }).then((res) => res.json());

  const streetField = document.getElementById('mm-input-street-field') as HTMLInputElement;
  if (streetField) {
    streetField.value = addressDetails?.street;
    streetField.dispatchEvent(event);
  }
  const buildingNumberField = document.getElementById('mm-input-building-number-field') as HTMLInputElement;
  if (buildingNumberField) {
    buildingNumberField.value = addressDetails?.buildingNumber;
    buildingNumberField.dispatchEvent(event);
  }
  const cityField = document.getElementById('mm-input-city-field') as HTMLInputElement;
  if (cityField) {
    cityField.value = addressDetails?.city;
    cityField.dispatchEvent(event);
  }
  const postalCodeField = document.getElementById('mm-input-postal-code-field') as HTMLInputElement;
  if (postalCodeField) {
    postalCodeField.value = addressDetails?.postalCode;
    postalCodeField.dispatchEvent(event);
  }
  const countryField = document.getElementById('country-field') as HTMLInputElement;
  if (countryField) {
    countryField.value = DEFAULT_COUNTRIES_PER_MARKET[addressDetails?.country?.toLowerCase()];
    countryField.dispatchEvent(event);
  }

  // Track address selection
  dispatchGAEvent?.({
    event: 'se_using_address_search_field',
    action: 'using_address_search_field',
    label: 'fields prefilled',
    category: ApplicationEventType.Interaction,
  });
}

export const toggleFlyoutEventKey = 'toggleFlyoutEvent';
